import { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { Button } from '../ui/button';
import { Confirm } from '../ui/confirm';
import { DropDownMenu } from '../ui/dropDownMenu';
import type { DropDownMenuItem } from '../ui/dropDownMenu';
import { useLauncherGameLocale, useLauncherLocale } from '../../hooks/useLauncherLocale';
import { currencyService } from '../../services/currencies';
import { gameLanguagesService } from '../../services/gameLanguages';
import {
    DEFAULT_LOCALE,
    launcherPreferencesService,
} from '../../services/launcherPreferences';
import { languageService } from '../../services/languages';
import { gamesService } from '../../services/games';
import { Routes as AppRoutes } from '../../routes/routes';
import { usePreviewPublish } from '../../features/preview/PreviewPublishContext';
import type { LanguagesScope } from './types';
import { resolveGameLocale, toLanguageMenuItem } from './utils';

import styles from './Header.module.css';

const DEFAULT_LANGUAGE_MENU_ITEM: DropDownMenuItem = toLanguageMenuItem({
    name: 'English',
    isoName: DEFAULT_LOCALE,
    flag: 'US',
});

export function Header() {
    const mainLocale = useLauncherLocale();
    const gameLocale = useLauncherGameLocale();
    const gameDetailsMatch = useMatch(`/${AppRoutes.GAME}/:gameId`);
    const isPreviewPage = Boolean(useMatch(`/${AppRoutes.PREVIEW}`));
    const { canPublish, isLoggedIn, setCanPublish } = usePreviewPublish();
    const showPublishButton = isPreviewPage && isLoggedIn && canPublish !== null;
    const [isPublishing, setIsPublishing] = useState(false);
    const [showPublishConfirm, setShowPublishConfirm] = useState(false);
    const gameId = gameDetailsMatch?.params.gameId;
    const isGameDetailsPage = Boolean(gameId);
    const locale = isGameDetailsPage ? gameLocale : mainLocale;
    const [currency, setCurrency] = useState(() => launcherPreferencesService.getCurrency());
    const [languages, setLanguages] = useState<DropDownMenuItem[]>([]);
    const [languagesScope, setLanguagesScope] = useState<LanguagesScope | null>(null);
    const [currencies, setCurrencies] = useState<DropDownMenuItem[]>([]);

    useEffect(() => {
        let isMounted = true;
        const scope: LanguagesScope = gameId ? 'game' : 'main';
        setLanguages([]);
        setLanguagesScope(null);

        const loadLanguages = gameId
            ? gameLanguagesService.getLiveAvailableLanguages(gameId).then((loadedLanguages) => {
                  if (!loadedLanguages.length) {
                      return [DEFAULT_LANGUAGE_MENU_ITEM];
                  }

                  return loadedLanguages.map(toLanguageMenuItem);
              })
            : languageService.getAllLanguages().then((loadedLanguages) =>
                  loadedLanguages.map(toLanguageMenuItem)
              );

        loadLanguages
            .then((menuItems) => {
                if (!isMounted) {
                    return;
                }

                setLanguages(menuItems);
                setLanguagesScope(scope);
            })
            .catch((error: unknown) => {
                console.warn(
                    'Failed to load languages',
                    error instanceof Error ? error.message : error
                );
                if (!isMounted || !gameId) {
                    return;
                }

                setLanguages([DEFAULT_LANGUAGE_MENU_ITEM]);
                setLanguagesScope('game');
            });

        return () => {
            isMounted = false;
        };
    }, [gameId]);

    useEffect(() => {
        let isMounted = true;

        currencyService
            .getAllCurrencies()
            .then((loadedCurrencies) => {
                if (!isMounted) {
                    return;
                }

                setCurrencies(
                    loadedCurrencies.map((loadedCurrency) => ({
                        value: loadedCurrency.currencyCode,
                        label: loadedCurrency.currencyCode,
                        symbol: loadedCurrency.symbol,
                    }))
                );
            })
            .catch((error: unknown) => {
                console.warn(
                    'Failed to load currencies',
                    error instanceof Error ? error.message : error
                );
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!gameId || languagesScope !== 'game' || languages.length === 0) {
            return;
        }

        launcherPreferencesService.setGameLocale(
            resolveGameLocale({
                availableLocales: languages,
                preferredLocale: mainLocale,
                defaultLocale: DEFAULT_LOCALE,
            })
        );
    }, [gameId, languages, languagesScope, mainLocale]);

    useEffect(() => {
        if (isGameDetailsPage || languagesScope !== 'main' || languages.length === 0) {
            return;
        }

        if (!languages.some((item) => item.value === mainLocale)) {
            launcherPreferencesService.setLocale(languages[0].value);
        }
    }, [isGameDetailsPage, languages, languagesScope, mainLocale]);

    useEffect(() => {
        if (currencies.length === 0 || currencies.some((item) => item.value === currency)) {
            return;
        }

        const fallbackCurrency = currencies[0].value;
        launcherPreferencesService.setCurrency(fallbackCurrency);
        setCurrency(fallbackCurrency);
    }, [currencies, currency]);

    const handleLocaleChange = (nextLocale: string) => {
        if (isGameDetailsPage) {
            launcherPreferencesService.setGameLocale(nextLocale);
            return;
        }

        launcherPreferencesService.setLocale(nextLocale);
    };

    const handlePublishClick = () => {
        if (!isLoggedIn || canPublish === false || isPublishing) {
            return;
        }

        setShowPublishConfirm(true);
    };

    const handlePublishConfirm = async () => {
        if (!isLoggedIn) {
            return;
        }

        setShowPublishConfirm(false);
        setIsPublishing(true);

        try {
            await gamesService.publishPreview();
            setCanPublish(false);
        } catch (error: unknown) {
            console.error(
                'Failed to publish draft games',
                error instanceof Error ? error.message : error
            );
        } finally {
            setIsPublishing(false);
        }
    };

    const isPublished = canPublish === false;
    const isPublishDisabled = isPublished || isPublishing;

    return (
        <header className={styles.header}>
            <div className={styles.menuButtons}>
                <DropDownMenu
                    items={languages}
                    value={locale}
                    onChange={handleLocaleChange}
                    ariaLabel="Select locale"
                />
                <DropDownMenu
                    items={currencies}
                    value={currency}
                    onChange={(nextCurrency) => {
                        launcherPreferencesService.setCurrency(nextCurrency);
                        setCurrency(nextCurrency);
                    }}
                    ariaLabel="Select currency"
                />
                {showPublishButton ? (
                    <Button
                        type="button"
                        variant="secondary"
                        color="purple"
                        disabled={isPublishDisabled}
                        onClick={handlePublishClick}
                        aria-busy={isPublishing}
                    >
                        {isPublishing ? (
                            <span className={styles.publishSpinner} aria-hidden="true" />
                        ) : isPublished ? (
                            'PUBLISHED'
                        ) : (
                            'PUBLISH'
                        )}
                    </Button>
                ) : null}
            </div>
            <Confirm
                open={showPublishButton && showPublishConfirm}
                title="Publish Changes?"
                message="Are you sure you want to publish these changes? They will become available in the Demo Application and replace the currently published version."
                onCancel={() => setShowPublishConfirm(false)}
                onConfirm={() => void handlePublishConfirm()}
            />
            <div className={styles.content}>
                <div className={styles.logoContainer}>
                    <img src="/logo.svg" alt="Atemoya Games Logo" className={styles.logo} />
                </div>
                <p className={styles.slogan}>Experience our collection of premium slot games</p>
            </div>
        </header>
    );
}
